import React, { useState, createContext, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Nominees from './components/Beneficiaries';
import Account from './components/Account';
import AdminToolbar from './components/AdminToolbar';
import AuditLog from './components/AuditLog';
import DemoSwitcher from './components/AdminToolbar'; // reuse
import Login from './components/Login';
import Register from './components/Register';
import ProfilePage from './components/ProfilePage';
import FinancialAssets from './components/FinancialAssets';
import LegalAssets from './components/LegalAssets';
import OtherAssets from './components/OtherAssets';
import AuthCallback from './components/AuthCallback';
import Logout from './components/Logout';
import NomineeInvite from './components/NomineeInvite';
import NomineeHome from './components/NomineeHome';
import NomineeEstateFinancial from './components/NomineeEstateFinancial';
import NomineeEstateLegal from './components/NomineeEstateLegal';
import NomineeEstateOther from './components/NomineeEstateOther';
import ChooseContext from './components/ChooseContext';
import AdminInspectorFormatter from './components/AdminInspectorFormatter';
import UIDemo from './components/UIDemo';
import api, { refreshCsrfToken } from './utils/api';

export const UserContext = createContext();

const AUDIT_FEATURE_ENABLED = String(process.env.REACT_APP_FEATURE_AUDIT || '').trim() === '1';
const UI_DEMO_FEATURE_ENABLED = String(process.env.REACT_APP_FEATURE_UI_DEMO || '').trim() === '1';

function App() {
  // Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);

  // NOTE: /auth/users is a legacy debug endpoint and is not used for nominee
  // authorization or role routing. Keep `users` in context for compatibility
  // with older components, but do not fetch it.

  // Restore session via HttpOnly cookies: bootstrap CSRF + profile from the API.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Skip the /auth/me probe entirely when no session cookie and no stored
        // bearer token exist — it would just be a guaranteed 401.
        const hasSessionCookie = typeof document !== 'undefined'
          && /(?:^|;\s*)pv_session=/.test(document.cookie || '');
        const hasBearer = typeof window !== 'undefined'
          && !!window.sessionStorage.getItem('pv_bearer_token');
        if (!hasSessionCookie && !hasBearer) {
          if (!cancelled) setCurrentUser(null);
          return;
        }
        await refreshCsrfToken();
        const { data } = await api.get('/auth/me');
        if (cancelled) return;
        setCurrentUser({
          id: data.id,
          email: data.email,
          name: data.name,
          role: data.role,
          type: data.activeContext,
          availableContexts: data.availableContexts || null,
          nomineeEstates: data.nomineeEstates || [],
          activeOwnerSub: data.activeOwnerSub || null,
          marketingConsent: data.marketingConsent,
          marketingConsentAt: data.marketingConsentAt,
          marketingConsentVersion: data.marketingConsentVersion,
          isAdmin: !!data.isAdmin,
        });
        try {
          window.localStorage.setItem('currentUser', JSON.stringify({
            id: data.id,
            email: data.email,
            name: data.name,
            role: data.role,
            type: data.activeContext,
            availableContexts: data.availableContexts || null,
            nomineeEstates: data.nomineeEstates || [],
            activeOwnerSub: data.activeOwnerSub || null,
            marketingConsent: data.marketingConsent,
            marketingConsentAt: data.marketingConsentAt,
            marketingConsentVersion: data.marketingConsentVersion,
            isAdmin: !!data.isAdmin,
          }));
        } catch {}
      } catch {
        if (!cancelled) {
          setCurrentUser(null);
          try { window.localStorage.removeItem('currentUser'); } catch {}
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <UserContext.Provider value={{ currentUser, setCurrentUser, users, setUsers }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/nominee-invite" element={<NomineeInvite />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/choose-context" element={currentUser ? <ChooseContext /> : <Login />} />
          <Route path="/nominee" element={currentUser ? <NomineeHome /> : <Login />} />
          <Route path="/nominee/estate/:ownerSub/financial" element={currentUser ? <NomineeEstateFinancial /> : <Login />} />
          <Route path="/nominee/estate/:ownerSub/legal" element={currentUser ? <NomineeEstateLegal /> : <Login />} />
          <Route path="/nominee/estate/:ownerSub/other" element={currentUser ? <NomineeEstateOther /> : <Login />} />
          <Route path="/dashboard" element={currentUser ? <Dashboard /> : <Login />} />
          <Route path="/financial-assets" element={currentUser ? <FinancialAssets /> : <Login />} />
          <Route path="/legal-assets" element={currentUser ? <LegalAssets /> : <Login />} />
          <Route path="/other-assets" element={currentUser ? <OtherAssets /> : <Login />} />
          {/* Legacy route: nominees management now lives under /account?tab=nominees */}
          <Route path="/nominees" element={currentUser ? <Navigate to="/account?tab=nominees" replace /> : <Login />} />
          <Route path="/owners" element={currentUser ? <Nominees /> : <Login />} />
          <Route path="/account" element={currentUser ? <Account /> : <Login />} />
          <Route path="/audit" element={currentUser ? (AUDIT_FEATURE_ENABLED ? <AuditLog /> : <Navigate to="/dashboard" replace />) : <Login />} />
          <Route path="/profile" element={currentUser ? <ProfilePage /> : <Login />} />
          <Route path="/admin/inspector" element={currentUser ? <AdminInspectorFormatter /> : <Login />} />
          <Route path="/ui" element={currentUser ? (UI_DEMO_FEATURE_ENABLED ? <UIDemo /> : <Navigate to="/dashboard" replace />) : <Login />} />
          {/* Legacy onboarding route: redirect to the canonical Cognito-backed signup UI. */}
          <Route path="/onboard" element={<Register />} />
          <Route path="/" element={<Login />} />
        </Routes>
      </BrowserRouter>
    </UserContext.Provider>
  );
}

export default App;